import React, { useEffect, useState } from 'react';
import { Button, Checkbox } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { useStoreState } from 'easy-peasy';
import { getFormTemplate, getClientFormAnswers } from './intakeForm.service';
import { formQuestionTypeEnum } from '../../helper/constants.helper';
import { database, auth } from '../../helper/firebase.helper';
import Loader from '../../components/loader/Loader';
import DisplayPdf from '../shared/DisplayPdf';
import CheckBox from './checkbox/CheckBox';
import Mcq from './mcq/Mcq';
import TextInput from './text-input/TextInput';
import TextNote from './text-note/TextNote';
import TopNav from '../../components/top-nav/TopNav';
import EmailInput from './email-input/EmailInput';
import DateInput from './date-input/DateInput';
import PhoneNumberInput from './phone-number/PhoneNumber';

const IntakeForm = () => {
  const client = useStoreState((state) => state.user);
  const { clientId, formId } = useParams();
  const [form, setForm] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchForm = async () => {
    setLoading(true);
    try {
      const [template, clientForm] = await Promise.all([
        getFormTemplate(formId),
        getClientFormAnswers(clientId, formId)
      ]);
      if (!template) {
        toast.error('Form not found');
        return;
      }
      const answersById = clientForm?.answers || {};
      const questionsWithAnswers = Array.isArray(template.questions)
        ? template.questions.map((q) => ({
            ...q,
            answer:
              answersById[q.id] !== undefined
                ? answersById[q.id]
                : q.option_type === formQuestionTypeEnum.CHECKBOX
                  ? []
                  : ''
          }))
        : [];
      setForm({
        ...template,
        questions: questionsWithAnswers
      });
    } catch (error) {
      console.log(error.message);
      toast.error('Error fetching form data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForm();
  }, [clientId, formId]);

  const handleAnswerChange = (questionIndex, value) => {
    setForm((prev) => {
      if (!prev || !Array.isArray(prev.questions)) return prev;
      const questions = [...prev.questions];
      questions[questionIndex] = {
        ...questions[questionIndex],
        answer: value
      };
      return {
        ...prev,
        questions
      };
    });
  };

  const validateEmail = (email) => {
    if (!email) return false;
    const trimmed = email.trim();
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(trimmed);
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  const validateForm = () => {
    if (!form || !Array.isArray(form.questions)) return true;
    for (let i = 0; i < form.questions.length; i++) {
      const q = form.questions[i];
      const qNo = i + 1;
      if (q.option_type === formQuestionTypeEnum.EMAIL) {
        if (!q.answer || !String(q.answer).trim()) {
          const isMandatory = !!q.mandatory;
          if (isMandatory) {
            toast.error(`Please enter an email for question ${qNo}.`);
            return false;
          }
        } else if (!validateEmail(q.answer)) {
          toast.error(`Please enter a valid email for question ${qNo}.`);
          return false;
        }
        continue;
      }
      if (!q.mandatory) continue;
      switch (q.option_type) {
        case formQuestionTypeEnum.MCQ:
          if (!q.answer) {
            toast.error(`Please answer question ${qNo}.`);
            return false;
          }
          break;

        case formQuestionTypeEnum.CHECKBOX:
          if (!Array.isArray(q.answer) || q.answer.length === 0) {
            toast.error(`Please select at least one option for question ${qNo}.`);
            return false;
          }
          break;

        case formQuestionTypeEnum.TEXTINPUT:
          if (!q.answer || !String(q.answer).trim()) {
            toast.error(`Please fill out question ${qNo}.`);
            return false;
          }
          break;

        case formQuestionTypeEnum.PDF:
          if (!q.answer) {
            toast.error(`Please accept the terms on question ${qNo}.`);
            return false;
          }
          break;

        case formQuestionTypeEnum.EMAIL:
          if (!validateEmail(q.answer)) {
            toast.error(`Please enter a valid email for question ${qNo}.`);
            return false;
          }
          break;

        case formQuestionTypeEnum.DATE: {
          if (!q.answer) {
            toast.error(`Please select a date for question ${qNo}.`);
            return false;
          }
          const d = new Date(q.answer);
          if (isNaN(d.getTime())) {
            toast.error(`Please enter a valid date for question ${qNo}.`);
            return false;
          }
          break;
        }

        case formQuestionTypeEnum.PHONE_NUMBER:
          if (!validatePhone(q.answer)) {
            toast.error(`Please enter a valid phone number for question ${qNo}.`);
            return false;
          }
          break;

        default:
          break;
      }
    }

    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form) return;
    const isValid = validateForm();
    if (!isValid) return;
    setLoading(true);
    try {
      const now = Date.now();
      const answers = {};
      (form.questions || []).forEach((q) => {
        answers[q.id] = q.answer !== undefined ? q.answer : null;
      });
      const mainRef = doc(database, `clients/${clientId}/intake_forms/${formId}`);
      const latestPayload = {
        clientId,
        formId,
        submitted_by: clientId || client?.uid || null,
        update_time: now,
        answers
      };
      await setDoc(mainRef, latestPayload, { merge: true });
      const archivePayload = {
        ...latestPayload,
        creation_time: now
      };
      await addDoc(collection(database, 'client_forms_archived'), archivePayload);
      toast.success('Form saved successfully');
      setIsSubmitted(true);
    } catch (error) {
      console.log(error.message);
      toast.error('Error saving form data');
    } finally {
      setLoading(false);
    }
  };

  const isLoggedIn = !!auth?.currentUser;

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <TopNav />
          {isSubmitted ? (
            <div
              className="intake-forms-wrapper"
              style={{ textAlign: 'center', marginTop: '200px' }}>
              <h3 className="margin-top--md" style={{ fontWeight: '600' }}>
                Thank you for submitting the form.
              </h3>
              <p className="margin-top--md">
                {isLoggedIn ? (
                  <>
                    You can view your history in the dashboard{' '}
                    <Link to="/dashboard/history" style={{ color: 'blue' }}>
                      here
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    You can go to the home page{' '}
                    <Link to="/" style={{ color: 'blue' }}>
                      here
                    </Link>
                    .
                  </>
                )}
              </p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit}>
              <div className="intake-forms-wrapper">
                <h3 className="margin-top--md" style={{ fontWeight: '600' }}>
                  {form?.title}
                </h3>
                {form?.questions?.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="item-wrapper"
                    style={{ maxWidth: '900px' }}>
                    {item.option_type === formQuestionTypeEnum.MCQ && (
                      <div>
                        <div className="flex-container">
                          <p>{index + 1}.&nbsp;</p>
                          <p style={{ marginTop: '-3px' }}>
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                        <Mcq
                          item={item}
                          index={index}
                          onChange={(value) => handleAnswerChange(index, value)}
                        />
                      </div>
                    )}
                    {item.option_type === formQuestionTypeEnum.CHECKBOX && (
                      <div>
                        <div className="flex-container">
                          <p>{index + 1}.&nbsp;</p>
                          <p style={{ marginTop: '-3px' }}>
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                        <CheckBox
                          item={item}
                          onChange={(value) => handleAnswerChange(index, value)}
                        />
                      </div>
                    )}
                    {item.option_type === formQuestionTypeEnum.TEXTINPUT && (
                      <div>
                        <div className="flex-container">
                          <p>{index + 1}.&nbsp;</p>
                          <p style={{ marginTop: '-3px' }}>
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                        <div style={{ marginLeft: '17px' }}>
                          <TextInput
                            item={item}
                            onChange={(value) => handleAnswerChange(index, value)}
                          />
                        </div>
                      </div>
                    )}
                    {item.option_type === formQuestionTypeEnum.TEXTNOTE && (
                      <div className="flex-container align-center">
                        <div>{index + 1}.&nbsp;</div>
                        <TextNote item={item} />
                      </div>
                    )}
                    {item.option_type === formQuestionTypeEnum.PDF && (
                      <div>
                        <p>{index + 1}.</p>
                        <div style={{ marginLeft: '17px', marginTop: '-30px' }}>
                          <DisplayPdf resourceUrl={item.fileUrl} />
                          <p className="margin-top--md">
                            <Checkbox
                              checked={!!item.answer}
                              onChange={(e) => handleAnswerChange(index, e.target.checked)}
                              style={{ marginLeft: '-10px' }}
                            />
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                      </div>
                    )}
                    {item.option_type === formQuestionTypeEnum.EMAIL && (
                      <div>
                        <div className="flex-container">
                          <p>{index + 1}.&nbsp;</p>
                          <p style={{ marginTop: '-3px' }}>
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                        <div style={{ marginLeft: '17px' }}>
                          <EmailInput
                            item={item}
                            index={index}
                            onChange={(value) => handleAnswerChange(index, value)}
                          />
                        </div>
                      </div>
                    )}

                    {item.option_type === formQuestionTypeEnum.DATE && (
                      <div>
                        <div className="flex-container">
                          <p>{index + 1}.&nbsp;</p>
                          <p style={{ marginTop: '-3px' }}>
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                        <div style={{ marginLeft: '17px' }}>
                          <DateInput
                            item={item}
                            index={index}
                            onChange={(value) => handleAnswerChange(index, value)}
                          />
                        </div>
                      </div>
                    )}

                    {item.option_type === formQuestionTypeEnum.PHONE_NUMBER && (
                      <div>
                        <div className="flex-container">
                          <p>{index + 1}.&nbsp;</p>
                          <p style={{ marginTop: '-3px' }}>
                            {item.question}
                            {item?.mandatory && <sup style={{ color: 'red' }}>*</sup>}
                          </p>
                        </div>
                        <div style={{ marginLeft: '17px' }}>
                          <PhoneNumberInput
                            item={item}
                            index={index}
                            onChange={(value) => handleAnswerChange(index, value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex-container margin-top--md">
                  {form && (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ minWidth: '100px', textTransform: 'none' }}>
                      Submit
                    </Button>
                  )}
                </div>
              </div>
            </form>
          )}
        </>
      )}
      <style jsx>
        {`
          .intake-forms-wrapper {
            max-width: 900px;
            margin: 0 auto;
            padding: 0px 10px 30px 10px;
          }
          .item-wrapper {
            padding: 15px 0px;
          }
          @media only screen and (max-width: 600px) {
            .intake-forms-wrapper {
              padding: 0px 10px 30px 10px;
            }
            .item-wrapper {
              padding: 10px 0px;
            }
          }
        `}
      </style>
    </>
  );
};

export default IntakeForm;
